/**
 * Well types as Paelode sees them (Corbin, 2026-10-17: "disposal, injection and swd are all
 * the same thing"). The catalog keeps the regulator's own code (OCC 2D* → SWD, 2R* → INJ;
 * RRC by the UIC injection type), but SWD and INJ are ONE category, "Disposal", everywhere a
 * person picks or reads a type. The raw codes stay as an exact match in the Wells filter.
 */

export const DISPOSAL_WELL_TYPES = ['SWD', 'INJ'] as const;

/** The Wells filter value the API reads as `well_type IN ('SWD', 'INJ')`. */
export const DISPOSAL_FILTER = 'DISPOSAL';

export interface WellTypeOption {
  readonly value: string;
  readonly label: string;
}

/** The Wells page filter: one Disposal choice, then the exact codes as an advanced match. */
export const WELL_TYPE_FILTER_OPTIONS: readonly WellTypeOption[] = [
  { value: 'OIL', label: 'OIL' },
  { value: 'GAS', label: 'GAS' },
  { value: 'OG', label: 'OG' },
  { value: DISPOSAL_FILTER, label: 'Disposal (SWD/INJ)' },
];
export const WELL_TYPE_EXACT_OPTIONS: readonly WellTypeOption[] = [
  { value: 'SWD', label: 'SWD only' },
  { value: 'INJ', label: 'INJ only' },
];

/**
 * Add Well: one "Disposal" type, stored as SWD. The hauler's disposal-well authorization and
 * every disposal read accept SWD and INJ alike, so the code chosen for a hand-made well
 * changes nothing; SWD is the plain "salt-water disposal" a person adding one by hand means.
 */
export const ADD_WELL_TYPE_OPTIONS: readonly WellTypeOption[] = [
  { value: 'OIL', label: 'OIL' },
  { value: 'GAS', label: 'GAS' },
  { value: 'OG', label: 'OG' },
  { value: 'SWD', label: 'Disposal' },
];

/** How a stored type reads: "Disposal (SWD)" / "Disposal (INJ)", anything else as stored. */
export function wellTypeLabel(wellType: string | null): string | null {
  if (wellType === null) return null;
  const t = wellType.toUpperCase();
  return (DISPOSAL_WELL_TYPES as readonly string[]).includes(t) ? `Disposal (${t})` : wellType;
}
